import { isValid, parse } from 'date-fns';
import { RuleProps } from 'types/application';

export const ACADEMIC_QUALIFICATION = 'academic-qualification';
export const LANGUAGE_PROFICIENCY = 'language-proficiency';
export const CURRENT_EMPLOYMENT_DETAILS = 'current-employment-details';
export const PERIOD_OF_EMPLOYMENT = 'period-of-employment';
export const ADDRESS_OF_EMPLOYER = 'address-of-employer';

export const ACADEMIC_QUALIFICATION_FIELDS = [
    {
        sectionID: ACADEMIC_QUALIFICATION,
        sectionName: '',
        fieldList: [
            {
                id: 'name-of-institution',
                label: 'Name of Institution',
                type: 'text',
                rule: {
                    required: true
                },
                span: 6
            },
            {
                id: 'country',
                apiID: 'Country',
                label: 'Country',
                type: 'dropdown',
                span: 6
            },
            {
                id: 'highest-qualification-attained',
                apiID: 'Qualification',
                label: 'Highest Qualification Attained',
                type: 'dropdown',
                rule: {
                    required: true
                },
                span: 6
            },
            {
                id: 'other-qualification-attainted',
                label: 'Other Qualification Attainted',
                type: 'text',
                rule: {
                    baseIds: [`${ACADEMIC_QUALIFICATION}-highest-qualification-attained`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(
                            baseValues && baseValues[`${ACADEMIC_QUALIFICATION}-highest-qualification-attained`] === 'QF7'
                        );
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(
                            baseValues && baseValues[`${ACADEMIC_QUALIFICATION}-highest-qualification-attained`] !== 'QF7'
                        );
                        return result;
                    }
                },
                span: 6
            },
            {
                id: 'name-of-course',
                label: 'Name of Course',
                type: 'text',
                rule: {
                    required: true
                },
                span: 6
            },
            {
                id: 'year-of-completion',
                label: 'Year of Completion',
                type: 'number',
                rule: {
                    charLength: 4,
                    required: true
                },
                span: 6
            },
            {
                id: 'attachments',
                label: 'Resume Upload',
                type: 'file',
                category: 'SupportingDocuments',
                fileTypes: ['.jpeg', '.jpg', '.png', '.bmp', '.mp4', '.mpeg', '.mov', '.pdf', '.xls', '.doc', '.xlsx', '.docx'],
                mimeTypes: [
                    'application/pdf',
                    'application/vnd.ms-excel',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    // 'text/plain',
                    'image/jpeg',
                    'image/jpg',
                    'image/png',
                    // 'image/gif',
                    'image/bmp',
                    'video/quicktime',
                    'video/mp4',
                    'video/mpeg'
                ],
                span: 6
            }
        ]
    }
];

export const LANGUAGE_PROFICIENCY_FIELDS = [
    {
        sectionID: LANGUAGE_PROFICIENCY,
        sectionName: '',
        fieldList: [
            {
                id: 'language-proficiency',
                label: 'Language Proficiency',
                type: 'table',
                rule: { required: true },
                subFields: [
                    {
                        id: 'language',
                        apiID: 'LanguageProficiency',
                        header: 'Language Proficiency',
                        type: 'dropdown',
                        rule: {
                            required: true
                        }
                    },
                    {
                        id: 'other-language',
                        header: 'Other Language Proficiency',
                        type: 'text',
                        rule: {
                            baseIds: [`${LANGUAGE_PROFICIENCY}-language-proficiency.idx.language`],
                            required: ({ baseValues, idx }: RuleProps) => {
                                const key = `${LANGUAGE_PROFICIENCY}-language-proficiency.${idx}.language`;
                                return baseValues?.[key] === 'NA';
                            },
                            hidden: ({ baseValues, idx }: RuleProps) => {
                                const key = `${LANGUAGE_PROFICIENCY}-language-proficiency.${idx}.language`;
                                return baseValues?.[key] !== 'NA';
                            }
                        }
                    },
                    {
                        id: 'written',
                        header: 'Written',
                        type: 'checkbox'
                    },
                    {
                        id: 'spoken',
                        header: 'Spoken',
                        type: 'checkbox'
                    }
                ],
                span: 12,
                buttonTitle: 'Add'
            }
        ]
    }
];

export const CURRENT_EMPLOYMENT_DETAILS_FIELDS = [
    {
        sectionID: CURRENT_EMPLOYMENT_DETAILS,
        sectionName: '',
        fieldList: [
            {
                id: 'name-of-employer',
                label: 'Name of Employer',
                type: 'text',
                span: 6
            },
            {
                id: 'type-of-employment',
                apiID: 'EmploymentType',
                label: 'Type of Employment',
                type: 'dropdown',
                span: 6
            },
            {
                id: 'current-designation',
                label: 'Current Designation',
                type: 'text',
                span: 6
            }
        ]
    },
    {
        sectionID: PERIOD_OF_EMPLOYMENT,
        sectionName: 'Period of Employment',
        fieldList: [
            {
                id: 'start-date',
                label: 'Start Date',
                type: 'date2',
                rule: {
                    customValidate: ({ currentValue }: RuleProps) => {
                        if (!currentValue) return;

                        const selectedDate = parse(currentValue as string, 'yyyy-MM-dd', new Date());
                        if (!isValid(selectedDate)) return 'Invalid Date';

                        return true;
                    }
                }
            }
        ]
    },
    {
        sectionID: ADDRESS_OF_EMPLOYER,
        sectionName: 'Address of Employer',
        fieldList: [
            {
                id: 'address',
                label: 'Address',
                type: 'longText',
                textRows: 4,
                span: 12
            },
            {
                id: 'country',
                apiID: 'Country',
                label: 'Country',
                type: 'dropdown',
                span: 6
            },
            {
                id: 'office-no',
                label: 'Office No.',
                type: 'combo',
                preField: {
                    id: 'country-code',
                    label: 'Coutry Code',
                    apiID: 'MobileNoCountryCode',
                    type: 'dropdown',
                    options: [{ optID: '65', optLabel: '+65' }],
                    defaultValue: '65',
                    rule: { disabled: true }
                },
                mainField: {
                    id: 'main',
                    type: 'number',
                    rule: {
                        baseIds: [`${ADDRESS_OF_EMPLOYER}-office-no-country-code`],
                        pattern: ({ baseValues }: RuleProps) => {
                            const isSG = Boolean(baseValues && baseValues[`${ADDRESS_OF_EMPLOYER}-office-no-country-code`] === '65');
                            return isSG ? /^[689][0-9]{7}$/ : undefined;
                        },
                        charLength: ({ baseValues }: RuleProps) => {
                            const isSG = Boolean(baseValues && baseValues[`${ADDRESS_OF_EMPLOYER}-office-no-country-code`] === '65');
                            return isSG ? 8 : 12;
                        }
                    }
                },
                span: 6
            }
        ]
    }
];

export const RCA_REGISTER_DETAILS_FIELDS = [
    {
        sectionID: ACADEMIC_QUALIFICATION,
        sectionName: 'Academic Qualification',
        fieldList: [
            {
                id: 'name-of-institution',
                label: 'Name of Institution',
                type: 'text',
                rule: {
                    required: true
                },
                span: 6
            },
            {
                id: 'country',
                apiID: 'Country',
                label: 'Country',
                type: 'dropdown',
                span: 6
            },
            {
                id: 'highest-qualification-attained',
                apiID: 'Qualification',
                label: 'Highest Qualification Attained',
                type: 'dropdown',
                rule: {
                    required: true
                },
                span: 6
            },
            {
                id: 'other-qualification-attainted',
                label: 'Other Qualification Attainted',
                type: 'text',
                rule: {
                    baseIds: [`${ACADEMIC_QUALIFICATION}-highest-qualification-attained`],
                    required: ({ baseValues }: RuleProps) => {
                        const result = Boolean(
                            baseValues && baseValues[`${ACADEMIC_QUALIFICATION}-highest-qualification-attained`] === 'QF7'
                        );
                        return result;
                    },
                    hidden: ({ baseValues }: RuleProps) => {
                        const result = Boolean(
                            baseValues && baseValues[`${ACADEMIC_QUALIFICATION}-highest-qualification-attained`] !== 'QF7'
                        );
                        return result;
                    }
                },
                span: 6
            },
            {
                id: 'name-of-course',
                label: 'Name of Course',
                type: 'text',
                rule: {
                    required: true
                },
                span: 6
            },
            {
                id: 'year-of-completion',
                label: 'Year of Completion',
                type: 'number',
                rule: {
                    charLength: 4,
                    required: true
                },
                span: 6
            },
            {
                id: 'attachments',
                label: 'Resume Upload',
                type: 'file',
                category: 'SupportingDocuments',
                fileTypes: ['.jpeg', '.jpg', '.png', '.bmp', '.mp4', '.mpeg', '.mov', '.pdf', '.xls', '.doc', '.xlsx', '.docx'],
                mimeTypes: [
                    'application/pdf',
                    'application/vnd.ms-excel',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    // 'text/plain',
                    'image/jpeg',
                    'image/jpg',
                    'image/png',
                    // 'image/gif',
                    'image/bmp',
                    'video/quicktime',
                    'video/mp4',
                    'video/mpeg'
                ],
                span: 6
            }
        ]
    },
    {
        sectionID: LANGUAGE_PROFICIENCY,
        sectionName: 'Language Proficiency',
        fieldList: [
            {
                id: 'language-proficiency',
                label: 'Language Proficiency',
                type: 'table',
                rule: { required: true },
                subFields: [
                    {
                        id: 'language',
                        apiID: 'LanguageProficiency',
                        header: 'Language Proficiency',
                        type: 'dropdown',
                        rule: {
                            required: true
                        }
                    },
                    {
                        id: 'other-language',
                        header: 'Other Language Proficiency',
                        type: 'text',
                        rule: {
                            baseIds: [`${LANGUAGE_PROFICIENCY}-language-proficiency.idx.language`],
                            required: ({ baseValues, idx }: RuleProps) => {
                                const key = `${LANGUAGE_PROFICIENCY}-language-proficiency.${idx}.language`;
                                return baseValues?.[key] === 'NA';
                            },
                            hidden: ({ baseValues, idx }: RuleProps) => {
                                const key = `${LANGUAGE_PROFICIENCY}-language-proficiency.${idx}.language`;
                                return baseValues?.[key] !== 'NA';
                            }
                        }
                    },
                    {
                        id: 'written',
                        header: 'Written',
                        type: 'checkbox'
                    },
                    {
                        id: 'spoken',
                        header: 'Spoken',
                        type: 'checkbox'
                    }
                ],
                span: 12,
                buttonTitle: 'Add'
            }
        ]
    },
    {
        sectionID: CURRENT_EMPLOYMENT_DETAILS,
        sectionName: 'Current Employment Details',
        fieldList: [
            {
                id: 'name-of-employer',
                label: 'Name of Employer',
                type: 'text',
                span: 6
            },
            {
                id: 'type-of-employment',
                apiID: 'EmploymentType',
                label: 'Type of Employment',
                type: 'dropdown',
                span: 6
            },
            {
                id: 'current-designation',
                label: 'Current Designation',
                type: 'text',
                span: 6
            }
        ]
    },
    {
        sectionID: PERIOD_OF_EMPLOYMENT,
        sectionName: 'Period of Employment',
        fieldList: [
            {
                id: 'start-date',
                label: 'Start Date',
                type: 'date2',
                rule: {
                    customValidate: ({ currentValue }: RuleProps) => {
                        if (!currentValue) return;

                        const selectedDate = parse(currentValue as string, 'yyyy-MM-dd', new Date());
                        if (!isValid(selectedDate)) return 'Invalid Date';

                        return true;
                    }
                }
            }
        ]
    },
    {
        sectionID: ADDRESS_OF_EMPLOYER,
        sectionName: 'Address of Employer',
        fieldList: [
            {
                id: 'address',
                label: 'Address',
                type: 'longText',
                textRows: 4,
                span: 12
            },
            {
                id: 'country',
                apiID: 'Country',
                label: 'Country',
                type: 'dropdown',
                span: 6
            },
            {
                id: 'office-no',
                label: 'Office No.',
                type: 'combo',
                preField: {
                    id: 'country-code',
                    label: 'Coutry Code',
                    apiID: 'MobileNoCountryCode',
                    type: 'dropdown',
                    options: [{ optID: '65', optLabel: '+65' }],
                    defaultValue: '65',
                    rule: { disabled: true }
                },
                mainField: {
                    id: 'main',
                    type: 'number',
                    rule: {
                        baseIds: [`${ADDRESS_OF_EMPLOYER}-office-no-country-code`],
                        pattern: ({ baseValues }: RuleProps) => {
                            const isSG = Boolean(baseValues && baseValues[`${ADDRESS_OF_EMPLOYER}-office-no-country-code`] === '65');
                            return isSG ? /^[689][0-9]{7}$/ : undefined;
                        },
                        charLength: ({ baseValues }: RuleProps) => {
                            const isSG = Boolean(baseValues && baseValues[`${ADDRESS_OF_EMPLOYER}-office-no-country-code`] === '65');
                            return isSG ? 8 : 12;
                        }
                    }
                },
                span: 6
            }
        ]
    }
];
